import React, { useState } from 'react';
import { CheckCircle, AlertCircle, Loader, HeartPulse, HardDrive } from 'lucide-react';
import apiClient from '../../utils/apiClient';
import './FileManagementSettings.css';

export const FileManagementSettings: React.FC = () => {
  const [error, setError] = useState<string>('');

  // Storage health state
  const [healthResult, setHealthResult] = useState<{
    ghostRecords: { file_id: number; canonical_filename: string; song_title: string }[];
    orphanFiles: string[];
    filenameMismatches: { file_id: number; canonical_filename: string; file_path: string }[];
    totalDbRecords: number;
    totalDiskFiles: number;
  } | null>(null);
  const [isCheckingHealth, setIsCheckingHealth] = useState(false);

  // Check storage health
  const handleCheckHealth = async () => {
    setIsCheckingHealth(true);
    setError('');
    try {
      const response = await apiClient.get('/file-management/storage-health');
      setHealthResult(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to check storage health');
    } finally {
      setIsCheckingHealth(false);
    }
  };

  return (
    <div className="file-management-settings">
      <div className="settings-header">
        <HardDrive size={24} />
        <h2>File Management</h2>
      </div>

      <p className="settings-description">
        Check the health of music file storage — find missing files, orphaned files, and database mismatches.
      </p>

      {/* Error Messages */}
      {error && (
        <div className="alert alert-error">
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      {/* Storage Health Check */}
      <div className="form-section">
        <label className="form-label">
          <HeartPulse size={18} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} />
          <strong>Storage Health</strong>
        </label>
        <button
          className="btn btn-secondary"
          onClick={handleCheckHealth}
          disabled={isCheckingHealth}
          style={{ marginBottom: 12 }}
        >
          {isCheckingHealth ? (
            <><Loader size={16} className="spinner" /> Checking...</>
          ) : (
            'Check Storage Health'
          )}
        </button>

        {healthResult && (
          <div className="health-results" style={{ fontSize: '0.9rem' }}>
            <p style={{ margin: '4px 0' }}>
              <strong>DB records:</strong> {healthResult.totalDbRecords} &nbsp;|&nbsp;
              <strong>Disk files:</strong> {healthResult.totalDiskFiles}
            </p>

            {healthResult.ghostRecords.length === 0 &&
             healthResult.orphanFiles.length === 0 &&
             healthResult.filenameMismatches.length === 0 ? (
              <div className="alert alert-success" style={{ marginTop: 8 }}>
                <CheckCircle size={18} />
                <span>All clean — no issues found.</span>
              </div>
            ) : (
              <>
                {healthResult.ghostRecords.length > 0 && (
                  <div className="alert alert-error" style={{ marginTop: 8 }}>
                    <AlertCircle size={18} />
                    <div>
                      <strong>Ghost records</strong> ({healthResult.ghostRecords.length}) — DB rows with no file on disk:
                      <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                        {healthResult.ghostRecords.map(g => (
                          <li key={g.file_id}>#{g.file_id} {g.canonical_filename} ({g.song_title})</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {healthResult.orphanFiles.length > 0 && (
                  <div className="alert alert-error" style={{ marginTop: 8 }}>
                    <AlertCircle size={18} />
                    <div>
                      <strong>Orphan files</strong> ({healthResult.orphanFiles.length}) — on disk but not in DB:
                      <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                        {healthResult.orphanFiles.map(f => (
                          <li key={f}>{f}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {healthResult.filenameMismatches.length > 0 && (
                  <div className="alert alert-error" style={{ marginTop: 8 }}>
                    <AlertCircle size={18} />
                    <div>
                      <strong>Filename mismatches</strong> ({healthResult.filenameMismatches.length}) — canonical_filename != file_path:
                      <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                        {healthResult.filenameMismatches.map(m => (
                          <li key={m.file_id}>#{m.file_id}: "{m.canonical_filename}" vs "{m.file_path}"</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
